import React, { useState, useEffect } from 'react';
import { Container, Grid, Button, Typography, Grow, Box, IconButton } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import useMediaQuery from '@mui/material/useMediaQuery';
import axios from 'axios';

// Use the API URL from environment variables
const apiUrl = process.env.REACT_APP_API_URL;

function HomePage() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:768px)');

  useEffect(() => {
    axios
      .get(`${apiUrl}/api/auth/status/`, { withCredentials: true })
      .then(response => {
        if (response.data.isAuthenticated) {
          setIsLoggedIn(true);
          localStorage.setItem('isAuthenticated', 'true');

          axios
            .get(`${apiUrl}/api/check_patreon_membership`, { withCredentials: true })
            .then(patreonResponse => {
              setHasAccess(patreonResponse.data.hasAccess);
              setLoading(false);
            })
            .catch(error => {
              console.error('Error checking access level:', error);
              setLoading(false);
            });
        } else {
          setIsLoggedIn(false);
          setHasAccess(false);
          localStorage.removeItem('isAuthenticated');
          setLoading(false);
        }
      })
      .catch(error => {
        console.error('Error fetching login status:', error);
        setLoading(false);
      });
  }, []);

  const handleCameraClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';
    input.style.display = 'none';

    input.onchange = (event) => {
      const file = event.target.files[0];
      console.log("📸 File selected:", file);
      console.log("📸 type:", file?.type, "size:", file?.size);
      if (file) {
        // optional: slight delay to ensure file is stable
        setTimeout(() => {
          navigate('/ingredient-assistant', { state: { imageFile: file } });
        }, 100); // small debounce to avoid Safari race
      }
    };

    document.body.appendChild(input);
    input.click();
    document.body.removeChild(input);
  };


  if (loading) return <p>Loading...</p>;

  return (
    <>
      <Grow in timeout={1000}>
        <Box sx={{ px: 2, width: '100%', maxWidth: 600, mx: 'auto' }}>
          <Grid container direction="column" alignItems="center" spacing={4} sx={{ py: 6 }}>
            <Grid item xs={12}>
              <Typography
                align="center"
                sx={{
                  fontFamily: 'EvelethCleanRegular',
                  fontWeight: 'bold',
                  fontSize: {
                    xs: '1.75rem',
                    sm: '2.25rem',
                    md: '3rem',
                  },
                  wordBreak: 'break-word',
                }}
              >
                Welcome to How to Drink!
              </Typography>
            </Grid>

            <Grid item xs={12} width="100%">
              <Button
                component={RouterLink}
                to="/drinks"
                variant="contained"
                color="secondary"
                fullWidth
                sx={{
                  fontWeight: 'bold',
                  borderRadius: '8px',
                  px: 3,
                  py: 2,
                  boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.3)',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    backgroundColor: '#BF0020',
                    boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.5)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                Drinks
              </Button>
            </Grid>

            <Grid item xs={12} width="100%">
              <Button
                component={RouterLink}
                to="/episodes"
                variant="contained"
                color="secondary"
                fullWidth
                sx={{
                  fontWeight: 'bold',
                  borderRadius: '8px',
                  px: 3,
                  py: 2,
                  boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.3)',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    backgroundColor: '#BF0020',
                    boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.5)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                Episodes
              </Button>
            </Grid>

            {isMobile && (
              <Grid item xs={12}>
                <Box textAlign="center">
                  <IconButton
                    onClick={handleCameraClick}
                    sx={{
                      backgroundColor: '#333',
                      color: '#fff',
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      boxShadow: '0px 4px 12px rgba(0,0,0,0.4)',
                      '&:hover': {
                        backgroundColor: '#555',
                      },
                    }}
                  >
                    <CameraAltIcon sx={{ fontSize: 32 }} />
                  </IconButton>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      </Grow>

      {!isLoggedIn && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backdropFilter: 'blur(10px)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              padding: '2rem',
              backgroundColor: '#404040',
              borderRadius: '16px',
              boxShadow: '0 8px 16px rgba(0,0,0,0.3)',
              textAlign: 'center',
            }}
          >
            <Typography variant="h5" gutterBottom>
              Please login to continue
            </Typography>
            <Button
              variant="contained"
              color="success"
              onClick={() => {
                // keep the user in the same tab
                window.location.href = `${apiUrl}/auth/login/patreon/?next=${encodeURIComponent(
                  window.location.href,
                )}`;
              }}
            >
              Login with Patreon
            </Button>
          </Box>
        </Box>
      )}

      {isLoggedIn && !hasAccess && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backdropFilter: 'blur(10px)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              padding: '2rem',
              backgroundColor: '#404040',
              borderRadius: '16px',
              boxShadow: '0 8px 16px rgba(0,0,0,0.3)',
              textAlign: 'center',
            }}
          >
            <Typography variant="h5" gutterBottom>
              You need to be an active patron or have PremiumUser access to view this content.
            </Typography>
            <Button href="https://www.patreon.com/" variant="contained" color="success">
              Become a Patron
            </Button>
          </Box>
        </Box>
      )}
    </>
  );
}

export default HomePage;
